import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Camera, Upload, ArrowRight, Loader2, CheckCircle2, Activity, ImageIcon, Info } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAssessment } from "@/context/AssessmentContext";
import { POSTURE_PATTERNS } from "@/lib/posturePatterns";

const VIEWS = [
  { key: "anterior", label: "Front" },
  { key: "right_lateral", label: "Right Side" },
  { key: "left_lateral", label: "Left Side" },
  { key: "posterior", label: "Back" },
];

const MODES = [
  { key: "picture", label: "Pick a picture", icon: ImageIcon, hint: "Easiest — no photo needed" },
  { key: "upload", label: "Upload photos", icon: Upload, hint: "AI analyzes your photos" },
  { key: "camera", label: "Use camera", icon: Camera, hint: "Live capture with skeleton overlay" },
];

export default function Assessment() {
  const [mode, setMode] = useState("picture");
  const [images, setImages] = useState({});
  const [pickedPattern, setPickedPattern] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [currentView, setCurrentView] = useState("anterior");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const streamRef = useRef(null);
  const poseRef = useRef(null);
  const rafRef = useRef(null);
  const [landmarksCount, setLandmarksCount] = useState(0);
  const [poseReady, setPoseReady] = useState(false);
  const navigate = useNavigate();
  const { update } = useAssessment();

  useEffect(() => {
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (poseRef.current) { try { poseRef.current.close(); } catch (e) {} }
    };
  }, []);

  const initPose = () => {
    if (poseRef.current || !window.Pose) return;
    try {
      const pose = new window.Pose({
        locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/pose@0.5/${file}`,
      });
      pose.setOptions({ modelComplexity: 1, smoothLandmarks: true, minDetectionConfidence: 0.5, minTrackingConfidence: 0.5 });
      pose.onResults((results) => {
        const canvas = overlayRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        canvas.width = results.image?.width || canvas.width;
        canvas.height = results.image?.height || canvas.height;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (results.poseLandmarks) {
          setLandmarksCount(results.poseLandmarks.length);
          if (window.drawConnectors && window.POSE_CONNECTIONS) {
            window.drawConnectors(ctx, results.poseLandmarks, window.POSE_CONNECTIONS, { color: "#F2CC8F", lineWidth: 3 });
          }
          if (window.drawLandmarks) {
            window.drawLandmarks(ctx, results.poseLandmarks, { color: "#E07A5F", fillColor: "#1B4332", lineWidth: 1, radius: 4 });
          }
        } else setLandmarksCount(0);
      });
      poseRef.current = pose;
      setPoseReady(true);
    } catch (e) { console.warn("Pose init failed", e); }
  };

  const runPoseLoop = () => {
    const loop = async () => {
      const v = videoRef.current;
      if (v && poseRef.current && v.readyState >= 2) {
        try { await poseRef.current.send({ image: v }); } catch (e) {}
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    loop();
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 720, height: 960, facingMode: "user" }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => { initPose(); runPoseLoop(); };
      }
      setCameraOn(true);
    } catch (e) {
      toast.error("Camera access denied. Try upload or pick a picture instead.");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setCameraOn(false);
    setLandmarksCount(0);
  };

  const capture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    // Downsize captured frame to 1024px longest edge, JPEG 80% — keeps payload small
    const MAX = 1024;
    const scale = Math.min(1, MAX / Math.max(v.videoWidth, v.videoHeight));
    c.width = Math.round(v.videoWidth * scale);
    c.height = Math.round(v.videoHeight * scale);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    const dataUrl = c.toDataURL("image/jpeg", 0.8);
    setImages((i) => ({ ...i, [currentView]: dataUrl }));
    toast.success(`Captured ${VIEWS.find((vw) => vw.key === currentView).label} view`);
  };

  const handleUpload = (viewKey, file) => {
    if (!file) return;
    // Downsize to max 1024px longest edge, JPEG 80% — keeps payload well under the
    // 100s ingress timeout for the vision analysis call.
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1024;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        const dataUrl = c.toDataURL("image/jpeg", 0.8);
        setImages((i) => ({ ...i, [viewKey]: dataUrl }));
      };
      img.onerror = () => {
        // Fallback: use the original file if resize fails
        setImages((i) => ({ ...i, [viewKey]: e.target.result }));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const analyze = async () => {
    const list = Object.entries(images);
    if (list.length === 0) { toast.error("Add at least one photo"); return; }
    setBusy(true);
    try {
      const { data } = await http.post("/analyze-vision", {
        images: list.map(([, v]) => v),
        view_labels: list.map(([k]) => k),
      }, { timeout: 90000 });
      update("vision", data);
      toast.success("Analysis complete");
      navigate("/posture-pattern");
    } catch (e) {
      // Show the actual server error when we have one
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" && detail
        ? detail
        : e.code === "ECONNABORTED"
        ? "Analysis timed out — please try again with just one clear side photo."
        : "Analysis failed — please retry with a clearer, well-lit photo.";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const goWithPicture = () => {
    if (!pickedPattern) { toast.error("Pick a picture that matches you first"); return; }
    // Store as pre-selected pattern hint; no vision analysis needed
    update("vision", {
      posture_score: null,
      severity_grade: null,
      summary: "Self-identified from picture",
      regions: {},
      likely_patterns: [{ id: pickedPattern.id, name: pickedPattern.name, confidence: 1 }],
    });
    navigate("/posture-pattern");
  };

  const count = Object.keys(images).length;

  return (
    <div className="animate-fade-in">
      <div className="flex items-center gap-2 mb-4 text-sm text-forest font-medium">
        <Camera className="w-4 h-4" /> Step 2 · Check Your Posture
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-forest mb-3">
        How do you want to check your posture?
      </h1>
      <p className="text-muted-foreground mb-8 max-w-3xl leading-relaxed">
        Choose the easiest way for you. If you're not sure, just <span className="text-forest font-medium">pick a picture</span> that looks like your posture — no photo needed.
      </p>

      {/* Mode selector */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
        {MODES.map((m) => {
          const Icon = m.icon;
          const active = mode === m.key;
          return (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              data-testid={`mode-${m.key}`}
              className={`text-left p-4 rounded-xl border transition-colors ${
                active ? "bg-forest text-bone border-forest" : "bg-white border-forest/15 hover:border-forest/40"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4" />
                <p className="font-heading font-semibold">{m.label}</p>
              </div>
              <p className={`text-xs ${active ? "text-bone/80" : "text-muted-foreground"}`}>{m.hint}</p>
            </button>
          );
        })}
      </div>

      {/* Picture mode */}
      {mode === "picture" && (
        <>
          <Card className="p-4 mb-4 bg-honey/10 border-honey/40 flex items-start gap-3">
            <Info className="w-5 h-5 text-forest shrink-0 mt-0.5" />
            <div className="text-sm text-ink">
              <p className="font-medium mb-1">How to pick:</p>
              <p className="text-muted-foreground">Look at yourself in a mirror or take a quick side-view selfie. Then click the picture below that looks the most like how you stand. Don't worry about being perfect.</p>
            </div>
          </Card>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {POSTURE_PATTERNS.map((p) => {
              const selected = pickedPattern?.id === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setPickedPattern(p)}
                  data-testid={`pick-${p.id}`}
                  className={`text-left bg-white rounded-xl overflow-hidden border transition-all group ${
                    selected ? "border-forest ring-2 ring-forest/30" : "border-forest/10 hover:border-forest/40"
                  }`}
                >
                  <div className="relative aspect-square bg-bone overflow-hidden">
                    <img src={p.image} alt={p.plain_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    {selected && (
                      <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-forest text-bone flex items-center justify-center">
                        <CheckCircle2 className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="font-heading font-semibold text-forest">{p.plain_name}</p>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mt-0.5 mb-2">{p.plain_region}</p>
                    <p className="text-xs text-ink/75 leading-relaxed">{p.plain_hint}</p>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="flex justify-end">
            <Button
              onClick={goWithPicture}
              disabled={!pickedPattern}
              className="bg-forest hover:bg-forest/90 text-bone rounded-full px-7"
              data-testid="picture-continue-btn"
            >
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </>
      )}

      {/* Upload + Camera modes */}
      {mode !== "picture" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            {mode === "camera" ? (
              <Card className="p-4 bg-forest/95 text-bone border-forest">
                <div className="relative bg-black rounded-lg overflow-hidden aspect-[3/4] flex items-center justify-center">
                  <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" />
                  <canvas ref={overlayRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{ objectFit: "cover" }} />
                  <canvas ref={canvasRef} className="hidden" />
                  {cameraOn && (
                    <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-forest/80 text-bone text-xs flex items-center gap-2 backdrop-blur-sm z-10" data-testid="landmark-hud">
                      <Activity className="w-3 h-3" />
                      {landmarksCount > 0 ? `Tracking · ${landmarksCount} points` : poseReady ? "Detecting…" : "Loading…"}
                    </div>
                  )}
                  {!cameraOn && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Button onClick={startCamera} className="bg-bone text-forest hover:bg-white" data-testid="start-camera-btn">
                        <Camera className="w-4 h-4 mr-2" /> Turn on camera
                      </Button>
                    </div>
                  )}
                </div>
                {cameraOn && (
                  <div className="mt-4 flex flex-wrap items-center gap-2 justify-between">
                    <div className="flex flex-wrap gap-2">
                      {VIEWS.map((v) => (
                        <button
                          key={v.key}
                          onClick={() => setCurrentView(v.key)}
                          className={`px-3 py-1 rounded-full text-xs border ${
                            currentView === v.key ? "bg-bone text-forest border-bone" : "border-bone/40 text-bone"
                          }`}
                          data-testid={`view-${v.key}`}
                        >
                          {v.label} {images[v.key] && "✓"}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={capture} className="bg-honey text-forest hover:bg-honey/90" data-testid="capture-btn">Snap</Button>
                      <Button onClick={stopCamera} variant="outline" className="border-bone/40 text-bone hover:bg-bone/10">Stop</Button>
                    </div>
                  </div>
                )}
              </Card>
            ) : (
              <>
                <Card className="p-4 mb-4 bg-honey/10 border-honey/40 flex items-start gap-3">
                  <Info className="w-5 h-5 text-forest shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium mb-1">Tips for good photos:</p>
                    <p className="text-muted-foreground">Wear tight/fitted clothes. Stand relaxed, arms at your sides. Get all four angles if you can — more angles means a better read.</p>
                  </div>
                </Card>
                <div className="grid grid-cols-2 gap-4">
                  {VIEWS.map((v) => (
                    <label
                      key={v.key}
                      className="border-2 border-dashed border-forest/25 hover:border-forest/60 rounded-xl aspect-[3/4] flex flex-col items-center justify-center cursor-pointer bg-white p-4 transition-colors relative overflow-hidden"
                    >
                      {images[v.key] ? (
                        <>
                          <img src={images[v.key]} alt={v.label} className="absolute inset-0 w-full h-full object-cover" />
                          <div className="absolute bottom-2 left-2 right-2 bg-forest/90 text-bone text-xs px-2 py-1 rounded flex items-center justify-between">
                            <span>{v.label}</span>
                            <CheckCircle2 className="w-3 h-3" />
                          </div>
                        </>
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-forest/60 mb-2" />
                          <p className="text-sm font-medium text-forest">{v.label} view</p>
                          <p className="text-xs text-muted-foreground mt-1">tap to upload</p>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid={`upload-${v.key}`}
                        onChange={(e) => handleUpload(v.key, e.target.files?.[0])}
                      />
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="lg:col-span-4">
            <Card className="p-6 bg-white border-forest/10 sticky top-24">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Progress</p>
              <p className="font-heading text-3xl font-semibold text-forest mb-1">{count}/4</p>
              <p className="text-sm text-muted-foreground mb-6">photos ready</p>

              <div className="space-y-2 mb-6">
                {VIEWS.map((v) => (
                  <div key={v.key} className="flex items-center justify-between text-sm">
                    <span>{v.label}</span>
                    {images[v.key] ? <CheckCircle2 className="w-4 h-4 text-sage" /> : <span className="text-muted-foreground text-xs">—</span>}
                  </div>
                ))}
              </div>

              <Button
                onClick={analyze}
                disabled={busy || count === 0}
                className="w-full bg-forest hover:bg-forest/90 text-bone rounded-full"
                data-testid="analyze-btn"
              >
                {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading photos…</>) : (<>Analyze my posture <ArrowRight className="w-4 h-4 ml-2" /></>)}
              </Button>
              <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
                Photos are processed once and not stored on our servers.
              </p>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
